import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';

// API Slices
import { useGetComplMastersQuery } from '../../features/complmaster/complmasterApiSlice';
import { useAddNewComplaintsMutation } from '../../features/complaints/complaintsApiSlice';
import { useGetSupportUsersQuery, useUpdateArraySupportUserMutation } from '../../features/supportusers/supportusersApiSlice';
import { useGetAssetMastersQuery } from '../../features/assetmasterApiSlice/assetmasterApiSlice';
import { useGetUsersQuery } from '../../features/users/usersApiSlice';
import { useLazyGetAssetsFinalFilteredQuery } from '../../features/addassets/addassetfinalApiSlice';

// Hooks
import useAuth from "../../features/hooks/useAuth";

// Styles & UI Components
import { HeadingWrap, Span, CommonButton } from '../../Styles/CommonStyles';

// UI Libraries
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// Redux
import { useSelector } from 'react-redux';

// Router
import { useNavigate } from 'react-router-dom';

// Utilities
import nameStringFn from '../../data/nameStringFn';
import { useGetofficesQuery } from '../../features/offices/officesApiSlice';

// Styled Components
const Ordiv = styled.div`
    margin-left: 40px;
    margin-top: 10px;
    margin-bottom: 10px;
`;

const ComplCatWrapper = styled.div`
    margin-top: 20px;
    margin-left: 10px;
`;

const SubmitWrapper = styled.div`
  margin-top: 20px;
  margin-left: 10px;
  margin-bottom: 20px;
`;

const OptionWrapper = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const TextInputWrap = styled.div`
  height: auto;
  margin-bottom: 30px;
`;

const StartAlignedSpan = styled.span`
  align-items: flex-start;
  margin-left: 20px;
  font-size: 20px;
  font-weight: 500;
`;

const SubHeadingField = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-left: 15px;
    margin-top: 50px;
    margin-bottom: 25px;
    font-weight: bold;
    font-family: "Roboto","Helvetica","Arial",sans-serif;
    font-size: 24px;
    font-weight: 900;
    color: #5C5891;  
`;

const Issuebutton = styled(CommonButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10vw;
  height: 50px;
  background-color: teal;
  margin-right: 30px;
  cursor: pointer;
  border-radius: 10px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorAlert = styled(Alert)`
  margin: 10px 20px;
`;

// Custom Hooks
const useComplaintForm = () => {
  const [formData, setFormData] = useState({
    selectedCateg: null,
    selectedAsset: null,
    selectedOemTag: null,
    complCateg: null,
    complSub: null,
    content: '',
    selectTagOption: ""
  });

  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      selectedCateg: null,
      selectedAsset: null,
      selectedOemTag: null,
      complCateg: null,
      complSub: null,
      content: '',
      selectTagOption: ""
    });
  }, []);

  return { formData, updateFormData, resetForm };
};

const useAssetFiltering = (Auth, userCurrentOffice) => {
  const [baseFilters, setBaseFilters] = useState({
    perm_cur_store: [],
    perm_alloc_ofc: [],
    perm_alloc_user: [],
    perm_cur_loc: ["user"],
    in_transit: "",
  });

  useEffect(() => {
    if (!Auth?.roles || !userCurrentOffice ) return;

    const mainRoles = Auth.roles?.main || [];
    const userRoles = Auth.roles[userCurrentOffice] || [];
  
    let updatedFilter = {
      perm_cur_store: [],
      perm_alloc_ofc: [],
      perm_alloc_user: [],
      perm_cur_loc: ["user"],
      in_transit: "",
    };
  
    if (mainRoles.includes("Admin") || mainRoles.includes("SuperAdmin")) {
      // Admin/SuperAdmin can see all assets
    } else if (userRoles.includes("level1approver") || userRoles.includes("level2approver") || userRoles.includes("assetadder")) {
      updatedFilter.perm_cur_store = [userCurrentOffice];
    } else if (userRoles.includes("UnitHead") || userRoles.includes("Bo")) {
      updatedFilter.perm_alloc_ofc = [userCurrentOffice];     
    } else if (mainRoles.length === 1 && mainRoles.includes("user")) {
      updatedFilter.perm_alloc_user = [Auth.id];
    }
    
    setBaseFilters(prevProps => {
      return JSON.stringify(prevProps) !== JSON.stringify(updatedFilter) ? updatedFilter : prevProps;
    });
  }, [Auth?.roles, userCurrentOffice, Auth?.id]);

  return { baseFilters };
};

// Asset Selector Component
const AssetSelector = React.memo(({ 
  formData, 
  updateFormData, 
  oemTagOptions, 
  assetOptions, 
  assetmaster,
  loading 
}) => {
  const handleSelectOemTag = useCallback((event, newValue, name) => {     
    if (name === "selectoemtag1") {
      updateFormData({ 
        selectTagOption: "option1", 
        selectedCateg: null,
        selectedOemTag: newValue 
      });
    }
    if (name === "selectoemtag2") {
      updateFormData({ 
        selectTagOption: "option2",
        selectedOemTag: newValue 
      });
    }      
    
    if (newValue === null) {
      updateFormData({ selectTagOption: "" });
    }
  }, [updateFormData]);

  return (
    <>
      {!formData.selectedCateg && (
        <OptionWrapper>
          <Autocomplete
            disablePortal
            id="oemtag-selector-1"
            size='small'
            value={formData.selectedOemTag}
            onChange={(event, newValue) => handleSelectOemTag(event, newValue, "selectoemtag1")}
            options={oemTagOptions}
            loading={loading.oemAssets}
            sx={{backgroundColor:"white", width:"400px", marginLeft:"20px", marginRight:"20px",height:"40px"}}
            isOptionEqualToValue={(option, value) => option.value._id === value.value._id}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Enter Asset OEM / ORG ID"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading.oemAssets ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </OptionWrapper>
      )}
      
      {formData.selectTagOption !== "option1" && formData.selectTagOption !== "" && (
        <Ordiv>OR</Ordiv>
      )}
      
      {formData.selectTagOption !== "option1" && (
        <OptionWrapper>
          <Autocomplete
            disablePortal
            id="category-selector"
            size='small'
            value={formData.selectedCateg}
            onChange={(event, newValue) => {
              updateFormData({ 
                selectedCateg: newValue, 
                selectedOemTag: null,
                selectedAsset: null 
              });
            }}        
            options={assetmaster ? Object.values(assetmaster).map(x => x.Category) : []}
            sx={{backgroundColor:"white", width:"400px", marginLeft:"20px", marginRight:"20px",height:"40px"}}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => <TextField {...params} label="Select Asset Category" />}
          />
          
          {formData.selectedCateg && (
            <Autocomplete
              disablePortal
              id="asset-selector"
              size='small'
              value={formData.selectedAsset}
              onChange={(event, newValue) => updateFormData({ selectedAsset: newValue })}
              options={assetmaster ? Object.values(assetmaster).find(x => x.Category === formData.selectedCateg)?.Asset?.map(y => y.Name) || [] : []}
              sx={{backgroundColor:"white", width:"400px", marginLeft:"20px", marginRight:"20px",height:"40px"}}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => <TextField {...params} label="Select Asset Name" />}
            />
          )}
          
          {formData.selectedAsset && (
            <Autocomplete
              disablePortal
              id="oemtag-selector-2"
              size='small'
              value={formData.selectedOemTag}
              onChange={(event, newValue) => handleSelectOemTag(event, newValue, "selectoemtag2")}
              options={assetOptions}
              loading={loading.categoryAssets}
              sx={{backgroundColor:"white", width:"400px", marginLeft:"20px", marginRight:"20px",height:"40px"}}
              isOptionEqualToValue={(option, value) => option.value._id === value.value._id}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Enter Asset OEM / ORG ID"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading.categoryAssets ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </OptionWrapper>
      )}
    </>
  );
});

// Complaint Categories Component
const ComplaintCategories = React.memo(({ 
  formData, 
  updateFormData, 
  filteredComplaintCategories, 
  complmaster 
}) => {
  return (
    <ComplCatWrapper>
      <SubHeadingField>Category and Subject of Complaint</SubHeadingField>
      <OptionWrapper>
        <Autocomplete
          disablePortal
          id="complaint-category"
          size="small"
          value={formData.complCateg}
          onChange={(event, newValue) => updateFormData({ complCateg: newValue, complSub: null })}
          options={filteredComplaintCategories}
          sx={{
            backgroundColor: "white",
            width: "400px",
            marginLeft: "20px",
            marginRight: "20px",
            height: "40px",
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Complaint Category" />
          )}
        />
        
        {formData.complCateg && (
          <Autocomplete
            disablePortal
            id="complaint-subject"
            size='small'
            value={formData.complSub}
            onChange={(event, newValue) => updateFormData({ complSub: newValue })}
            options={(complmaster && formData.complCateg) ? 
              Object.values(complmaster)?.find(x => x.Category === formData.complCateg)?.Subject || [] : 
              []
            }
            sx={{backgroundColor:"white", width:"400px", marginLeft:"20px", marginRight:"20px",height:"40px"}}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => <TextField {...params} label="Select Complaint Subject" />}
          />
        )}
      </OptionWrapper>
    </ComplCatWrapper>
  );
});

const RegisterComplaintOptimized = () => {
  const Auth = useAuth();
  const navigate = useNavigate();
  const userCurrentOffice = useSelector((state) => state.office.off);
  
  // Custom hooks
  const { formData, updateFormData, resetForm } = useComplaintForm();
  const { baseFilters } = useAssetFiltering(Auth, userCurrentOffice);

  console.log("Base Filters:", baseFilters);

  // Loading and error states
  const [loading, setLoading] = useState({
    oemAssets: false,
    categoryAssets: false,
    submitting: false
  });
  
  const [errors, setErrors] = useState({});
  const [successModal, setSuccessModal] = useState({ open: false, complaintNo: '' });
  const isSubmittingRef = useRef(false);

  // API Queries
  const { offices } = useGetofficesQuery("Offices", {
    selectFromResult: ({ data }) => ({
      offices: data?.entities,
    }),
  });

  const { assetmaster } = useGetAssetMastersQuery('arg', {
    selectFromResult: ({ data }) => ({
      assetmaster: data?.entities
    }),
  });

  const { complmaster } = useGetComplMastersQuery("complList", {
    selectFromResult: ({ data }) => ({
      complmaster: data ? data?.entities : []
    }),
  });

  const { supportusers } = useGetSupportUsersQuery("suppuserslist", {
    selectFromResult: ({ data }) => ({
      supportusers: data?.entities
    }),
  });

  const { user } = useGetUsersQuery("usersList", {
    selectFromResult: ({ data }) => ({
      user: data?.entities
    }),
  });

  const [addComplaint] = useAddNewComplaintsMutation();
  const [updateArraySupportUser] = useUpdateArraySupportUserMutation();

  // Lazy queries
  const [fetchOemTagAssets, { data: oemTagAssetsData }] = useLazyGetAssetsFinalFilteredQuery();
  const [fetchAssetsByCategory, { data: assetsByCategoryData }] = useLazyGetAssetsFinalFilteredQuery();

  // Memoized computations
  const oemTagOptions = useMemo(() => {
    if (!oemTagAssetsData?.assets) return [];
    
    return oemTagAssetsData.assets.map(x => ({
      label: x.oem_tag ? `${x.oem_tag} / ${x.org_tag}` : `${x.oem_tag}`,
      value: x
    }));
  }, [oemTagAssetsData?.assets]);

  const assetOptions = useMemo(() => {
    if (!assetsByCategoryData?.assets || !formData.selectedAsset) return [];
    
    return assetsByCategoryData.assets
      .filter(x => x.name === formData.selectedAsset)
      .map(x => ({
        label: `${x.oem_tag} / ${x.org_tag} (${x.cur_user_name})`,
        value: x
      }));
  }, [assetsByCategoryData?.assets, formData.selectedAsset]);

  const filteredComplaintCategories = useMemo(() => {
    if (!complmaster || !formData.selectedOemTag) return [];
    
    return Object.values(complmaster)
      .filter(doc => {
        const selectedCategory = formData.selectedOemTag?.value?.category;
        const selectedAssets = formData.selectedOemTag?.value?.name || [];

        const categoryMatch = doc.ApplAssetCateg?.includes(selectedCategory);
        const assetMatch = doc.AppAssets?.some(asset => 
          asset.toLowerCase() === "all assets" || selectedAssets?.includes(asset)
        );
        const hasSubjects = Array.isArray(doc.Subject) && doc.Subject.length > 0;

        return categoryMatch && assetMatch && hasSubjects;
      })
      .map(elm => elm.Category);
  }, [complmaster, formData.selectedOemTag]);

  const eligibleSupportStaff = useMemo(() => {
    if (!supportusers || !formData.complCateg) return [];
    
    return Object.values(supportusers)
      .filter(user => 
        (user?.complaintCategories === "ALL" || user?.complaintCategories?.includes(formData.complCateg)) &&
        user?.roles?.includes("SupportStaff")
      )
      .sort((a, b) => (a?.assignedComplaint?.length || 0) - (b?.assignedComplaint?.length || 0));
  }, [supportusers, formData.complCateg]);

  // Effects
  useEffect(() => {
    if (baseFilters && !formData.selectedCateg) {
      setLoading(prev => ({ ...prev, oemAssets: true }));
      fetchOemTagAssets({
        page: 1,
        limit: 1000,
        filters: baseFilters,
      }).finally(() => {
        setLoading(prev => ({ ...prev, oemAssets: false }));
      });
    }
  }, [baseFilters, formData.selectedCateg, fetchOemTagAssets]);

  useEffect(() => {
    if (formData.selectedAsset && formData.selectedCateg && baseFilters) {
      setLoading(prev => ({ ...prev, categoryAssets: true }));
      const categoryFilter = {
        ...baseFilters,
        name: formData.selectedAsset
      };
      
      fetchAssetsByCategory({
        page: 1,
        limit: 1000,
        filters: categoryFilter,
      }).finally(() => {
        setLoading(prev => ({ ...prev, categoryAssets: false }));
      });
    }
  }, [formData.selectedAsset, formData.selectedCateg, baseFilters, fetchAssetsByCategory]);

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    if (!formData.selectedOemTag) {
      newErrors.asset = "Please select an asset";
    }
    
    if (!formData.complCateg) {
      newErrors.category = "Please select a complaint category";
    }
    
    if (!formData.complSub) {
      newErrors.subject = "Please select a complaint subject";
    }
    
    if (!formData.content?.trim()) {
      newErrors.content = "Please provide complaint details";
    }
    
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  }, [formData]);

  // Form submission
  const onSubmitForm = async (e) => {
    e.preventDefault();

    if (isSubmittingRef.current) return;

    const { isValid, errors: validationErrors } = validateForm();
    
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    isSubmittingRef.current = true;

    try {
      setLoading(prev => ({ ...prev, submitting: true }));
      setErrors({});
      setSuccessModal({ open: false, complaintNo: '' });
      
      const assetCateg = formData.selectedCateg || formData.selectedOemTag?.value?.category;
      const noEligibleStaff = !Object.values(supportusers || {}).some(
        user => user?.assetCategories === "ALL" || user?.assetCategories?.includes(assetCateg)
      );

      if (noEligibleStaff) {
        throw new Error("No Support Staff Available for this Category");
      }

      const supportStaff = eligibleSupportStaff[0]?._id || eligibleSupportStaff[0]?.id || "";
      
      if (!supportStaff) {
        throw new Error("No available support staff found");
      }

      // Build complaint object
      const complaintData = {
        complaintNo: "",
        req_creator_id: Auth.id,
        req_creator_details: user && user[Auth.id] 
          ? `${user[Auth.id].name.firstName} ${user[Auth.id].name.middleName || ''} ${user[Auth.id].name.lastName || ''}`.trim() + 
            `-${userCurrentOffice}-${Auth.designation?.[userCurrentOffice] || 'Unknown'}`
          : "Unknown User",
        created_at:"",
        assetId: formData.selectedOemTag?.value?._id,
        assetName: formData.selectedAsset || formData.selectedOemTag?.value?.name,
        store: formData.selectedOemTag?.value?.pur_store,
        complaintCat: formData.complCateg,
        complaintSub: formData.complSub,
        complaintDesc: formData.content,
        curr_status: "Open",
        curr_sent_by: Auth.id,
        curr_sent_to: supportStaff,
        curr_sent_to_string: supportusers && supportusers[supportStaff] 
          ? `${supportusers[supportStaff].name?.firstName || ''} ${supportusers[supportStaff].name?.middleName || ''} ${supportusers[supportStaff].name?.lastName || ''}`.trim() +
            `-${supportusers[supportStaff]?.designation || 'Support Staff'}`
          : "Unknown Support Staff",
        curr_sent_to_tag: "complaint_created",
        curr_assigned_to: supportStaff,
        flow_users: [Auth.id],
        req_data: [{
          action: "Complaint Filed",
          sent_to_tag: "complaint_created",
          text: formData.content,
          sent_by: {
            id: Auth.id,
            name: user && user[Auth.id] ? user[Auth.id].name : { firstName: "Unknown", middleName: "", lastName: "" },
            office: userCurrentOffice,
            designation: Auth.designation?.[userCurrentOffice] || '',
          },
          sent_to: {
            id: supportStaff,
            name: supportusers && supportusers[supportStaff] ? 
              supportusers[supportStaff].name : 
              { firstName: "Unknown", middleName: "", lastName: "" },
            designation: supportusers && supportusers[supportStaff] ? 
              (supportusers[supportStaff]?.designation || 'Support Staff') : 
              'Support Staff',
          },
        }],
        opened: false
      };

      const res = await addComplaint(complaintData).unwrap();
      
      const updateBody = {
        id: supportStaff,
        action: "Push",
        arrayKey: "assignedComplaint",
        data: [res]
      };
      
      await updateArraySupportUser(updateBody).unwrap();
      
      setSuccessModal({ open: true, complaintNo: res.complaintNo });
      resetForm();
      
    } catch (error) {
      console.error("Complaint submission error:", error);
      const msg = error?.data?.message || error?.message || error?.error || 'Submission failed. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
      isSubmittingRef.current = false;
    }
  };

  return (
    <>
      <Dialog
        open={successModal.open}
        onClose={() => setSuccessModal({ open: false, complaintNo: '' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', paddingBottom: 0 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 1 }} />
          <div style={{ fontWeight: 700, fontSize: '1.3rem', color: '#2e7d32' }}>
            Complaint Lodged Successfully
          </div>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', paddingTop: '12px !important' }}>
          <div style={{ fontSize: '0.95rem', color: '#555', marginBottom: '8px' }}>
            Your complaint has been registered and assigned to a support staff member.
          </div>
          <div style={{
            display: 'inline-block',
            background: '#f0f7f0',
            border: '1px solid #a5d6a7',
            borderRadius: '8px',
            padding: '10px 20px',
            marginTop: '8px',
          }}>
            <span style={{ fontSize: '0.85rem', color: '#777' }}>Complaint Number</span>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1b5e20', letterSpacing: '0.5px' }}>
              {successModal.complaintNo}
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', paddingBottom: '16px' }}>
          <CommonButton
            style={{ backgroundColor: '#2e7d32', color: '#fff', borderRadius: '8px', padding: '8px 32px' }}
            onClick={() => {
              setSuccessModal({ open: false, complaintNo: '' });
              navigate('/management/unifiedsentbox?tab=4&subtab=0');
            }}
          >
            OK
          </CommonButton>
        </DialogActions>
      </Dialog>

      <HeadingWrap>
        <Span>Register Complaint</Span>
      </HeadingWrap>

      {errors.submit && (
        <ErrorAlert severity="error">
          {errors.submit}
        </ErrorAlert>
      )}
      
      <form onSubmit={onSubmitForm}>
        <AssetSelector
          formData={formData}
          updateFormData={updateFormData}
          oemTagOptions={oemTagOptions}
          assetOptions={assetOptions}
          assetmaster={assetmaster}
          loading={loading}
        />
        
        {formData.selectedOemTag && (
          <StartAlignedSpan>
            <strong>Asset Name: </strong> {`${formData.selectedOemTag.value.name},`} 
            <strong> Current Location:</strong> {`${formData.selectedOemTag?.value?.alloc_ofc} (${formData.selectedOemTag?.value?.cur_user_name})`}
          </StartAlignedSpan>
        )}

        {formData.selectedOemTag && (
          <ComplaintCategories
            formData={formData}
            updateFormData={updateFormData}
            filteredComplaintCategories={filteredComplaintCategories}
            complmaster={complmaster}
          />
        )}

        {formData.complSub && (
          <TextInputWrap>
            <HeadingWrap>
              <Span>Details</Span>
            </HeadingWrap>
            <div>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Enter complaint details..."
                variant="outlined"
                value={formData.content}
                onChange={(e) => updateFormData({ content: e.target.value })}
                error={!!errors.content}
                helperText={errors.content}
                sx={{ 
                  backgroundColor: "white",
                  marginRight: "40px",
                  '& .MuiOutlinedInput-root': {
                    minHeight: '15vh'
                  }
                }}
              />
            </div>
          </TextInputWrap>
        )}

        {formData.complSub && (
          <SubmitWrapper>
            <Issuebutton type='submit' disabled={loading.submitting}>
              {loading.submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Issuebutton>
          </SubmitWrapper>
        )}
      </form>
    </>
  );
};

export default RegisterComplaintOptimized; 